import React from 'react';
import { Button, Col, Space, Typography } from 'antd';
import { TeamOutlined, RightOutlined } from '@ant-design/icons';
import { useNavigate } from 'react-router-dom';
import { ControlTowerSectionCard } from '@/components/control-tower/ControlTowerSectionCard';
import { ControlTowerMetricStrip } from '@/components/control-tower/ControlTowerMetricStrip';
import { ControlTowerMetricTile } from '@/components/control-tower/ControlTowerMetricTile';
import type { HrmsDashboardActionSummary } from '@/services/hrmsDashboard.service';

const { Text } = Typography;

export const HrmsTalentSnapshot: React.FC<{
  summary: HrmsDashboardActionSummary;
}> = ({ summary }) => {
  const navigate = useNavigate();
  const t = summary.talent;
  if (!t?.enabled) return null;

  return (
    <ControlTowerSectionCard
      zone="qmsNcrQueue"
      sectionId="talent-snapshot"
      title={
        <Space>
          <TeamOutlined />
          Talent snapshot
        </Space>
      }
      extra={
        <Button type="link" size="small" icon={<RightOutlined />} onClick={() => navigate('/hr/recruitment')}>
          Open Talent Acquisition
        </Button>
      }
    >
      <Text type="secondary" style={{ display: 'block', marginBottom: 12 }}>
        Reuses Talent Acquisition home KPIs — open the TA Control home for full queues and funnel.
      </Text>
      <ControlTowerMetricStrip zone="metricsOrders" title="Pipeline">
        <Col xs={12} sm={8} md={6} lg={4}>
          <ControlTowerMetricTile
            title="Open requisitions"
            count={t.open_requisitions}
            urgency={t.open_requisitions > 0 ? 'attention' : 'neutral'}
            footerLink={{ label: 'Reqs', onClick: () => navigate('/hr/requisitions') }}
          />
        </Col>
        <Col xs={12} sm={8} md={6} lg={4}>
          <ControlTowerMetricTile
            title="TA approvals"
            count={t.pending_approvals}
            urgency={t.pending_approvals > 0 ? 'attention' : 'neutral'}
            footerLink={{ label: 'Home', onClick: () => navigate('/hr/recruitment') }}
          />
        </Col>
        <Col xs={12} sm={8} md={6} lg={4}>
          <ControlTowerMetricTile
            title="Active pipeline"
            count={t.active_pipeline}
            urgency={t.active_pipeline > 0 ? 'attention' : 'neutral'}
            footerLink={{ label: 'Apps', onClick: () => navigate('/hr/applications') }}
          />
        </Col>
        <Col xs={12} sm={8} md={6} lg={4}>
          <ControlTowerMetricTile
            title="Interviews 7d"
            count={t.interviews_next_7d}
            urgency={t.interviews_next_7d > 0 ? 'attention' : 'neutral'}
            footerLink={{ label: 'Calendar', onClick: () => navigate('/hr/interviews') }}
          />
        </Col>
        <Col xs={12} sm={8} md={6} lg={4}>
          <ControlTowerMetricTile
            title="Offers out"
            count={t.offers_awaiting_response}
            urgency={t.offers_awaiting_response > 0 ? 'attention' : 'neutral'}
            footerLink={{ label: 'Offers', onClick: () => navigate('/hr/offers') }}
          />
        </Col>
        <Col xs={12} sm={8} md={6} lg={4}>
          <ControlTowerMetricTile
            title="Onboarding overdue"
            count={t.overdue_onboarding_tasks}
            urgency={t.overdue_onboarding_tasks > 0 ? 'critical' : 'neutral'}
            extra={
              t.onboarding_in_progress > 0 ? (
                <Text type="secondary" style={{ fontSize: 11 }}>
                  {t.onboarding_in_progress} in progress
                </Text>
              ) : undefined
            }
            footerLink={{
              label: 'Onboarding',
              onClick: () => navigate('/hr/onboarding'),
            }}
          />
        </Col>
      </ControlTowerMetricStrip>
    </ControlTowerSectionCard>
  );
};

export default HrmsTalentSnapshot;
